/**
 * The namespace is an application interface for controlling navigation in a 3D window.
 * 
 * @namespace navigator
 * @memberof module:solo.app
 * @since 2.1.4
 * 
 * @prop {number} MOUSE_DRAG_MODE_SPIN=0
 * @prop {number} MOUSE_DRAG_MODE_PAN=1
 * @prop {number} MOUSE_DRAG_MODE_ZOOM=2
 * @prop {number} MOUSE_DRAG_MODE_DEFAULT=0
 */

function Navigator() {
    var objectFitDisabled = false,
        changeCenterpointDisabled = false,
        inertiaDisabled = false,
        inertiaDecelerationFactor = 1;

    function setOption(option, set) {
        var value = Module._tiramisu_getNavigatorOptions();
        if (set)
            value |= option;
        else
            value &= ~option;
        Module._tiramisu_setNavigatorOptions(value);
    }

    Object.defineProperties(this, {
        /**
         * Enables or disables the navigation in the 3D window.
         * 
         * @static
         * @member {boolean} navigationDisabled
         * @memberof module:solo.app.navigator
         */
        'navigationDisabled': {
            get: function () { return (Module._tiramisu_getNavigatorOptions() & 16) != 0; },
            set: function (value) { setOption(16, value); },
            enumerable: true
        },
        /**
         * Enables or disables zoom scale limits.
         * 
         * @static
         * @member {boolean} zoomScaleLimitsDisabled
         * @memberof module:solo.app.navigator
         */
        'zoomScaleLimitsDisabled': {
            get: function () { return (Module._tiramisu_getNavigatorOptions() & 4) != 0; },
            set: function (value) { setOption(4, value); },
            enumerable: true
        },
        /**
         * Locks the center of rotation when panning.
         * 
         * @static
         * @member {boolean} rotationCenterLocked
         * @memberof module:solo.app.navigator
         */
        'rotationCenterLocked': {
            get: function () { return (Module._tiramisu_getNavigatorOptions() & 8) != 0; },
            set: function (value) { setOption(8, value); },
            enumerable: true
        },
        /**
         * Uses the point under the mouse cursor as a zoom anchor.
         * 
         * > #### __NOTE__
         * >
         * > It is required to use the {@link module:solo.app DISABLE_DISCARDABLE_GEOMETRY_DATA} flag when initializing the library.
         * >```
         * >    Cortona3DSolo.use('core', { 
         * >        features: Cortona3DSolo.app.DISABLE_DISCARDABLE_GEOMETRY_DATA
         * >    });
         * >```
         * 
         * @static
         * @member {boolean} automaticZoomAnchor
         * @memberof module:solo.app.navigator
         */
        'automaticZoomAnchor': {
            get: function () { return (Module._tiramisu_getNavigatorOptions() & 1) != 0; },
            set: function (value) { setOption(1, value); },
            enumerable: true
        },
        /**
         * Uses the point under the mouse cursor as a spin center.
         * 
         * > #### __NOTE__
         * >
         * > It is required to use the {@link module:solo.app DISABLE_DISCARDABLE_GEOMETRY_DATA} flag when initializing the library.
         * >```
         * >    Cortona3DSolo.use('core', { 
         * >        features: Cortona3DSolo.app.DISABLE_DISCARDABLE_GEOMETRY_DATA
         * >    });
         * >```
         * 
         * @static
         * @member {boolean} automaticSpinCenter
         * @memberof module:solo.app.navigator
         */
        'automaticSpinCenter': {
            get: function () { return (Module._tiramisu_getNavigatorOptions() & 2) != 0; },
            set: function (value) { setOption(2, value); },
            enumerable: true
        },
        /**
         * Performs single step of navigation in the 3D window.
         * 
         * @static
         * @method spin
         * @param {number} offsetX
         * @param {number} offsetY
         * @memberof module:solo.app.navigator
         */
        'spin': {
            value: function (offsetX, offsetY) { Module._tiramisu_navigate(0, offsetX, offsetY); },
            enumerable: true
        },
        /**
         * Performs single step of navigation in the 3D window.
         * 
         * @static
         * @method pan
         * @param {number} translationX
         * @param {number} translationY
         * @memberof module:solo.app.navigator
         */
        'pan': {
            value: function (translationX, translationY) { Module._tiramisu_navigate(1, translationX, translationY); },
            enumerable: true
        },
        /**
         * Performs single step of navigation in the 3D window.
         * 
         * @static
         * @method zoom
         * @param {number} zoomScale
         * @memberof module:solo.app.navigator
         */
        'zoom': {
            value: function (zoomScale) { Module._tiramisu_navigate(2, zoomScale, 0); },
            enumerable: true
        },
        /**
         * Performs single step of navigation in the 3D window.
         * 
         * @static
         * @method rotate
         * @param {number} angle
         * @memberof module:solo.app.navigator
         */
        'rotate': {
            value: function (angle) { Module._tiramisu_navigate(3, angle, 0); },
            enumerable: true
        },
        /**
         * Sets or gets the mouse navigation mode:
         * - app.navigator.MOUSE_DRAG_MODE_SPIN = 0
         * - app.navigator.MOUSE_DRAG_MODE_PAN = 1
         * - app.navigator.MOUSE_DRAG_MODE_ZOOM = 2
         * 
         * @static
         * @member {number} mouseDragMode
         * @memberof module:solo.app.navigator
         */
        'mouseDragMode': {
            get: function () { return Module._tiramisu_getMouseDragMode(); },
            set: function (value) { Module._tiramisu_setMouseDragMode(value); },
            enumerable: true
        },
        'MOUSE_DRAG_MODE_SPIN': { value: 0, enumerable: true },
        'MOUSE_DRAG_MODE_PAN': { value: 1, enumerable: true },
        'MOUSE_DRAG_MODE_ZOOM': { value: 2, enumerable: true },
        'MOUSE_DRAG_MODE_DEFAULT': { value: 0, enumerable: true },

        /**
         * Sets or gets the spin speed factor.
         * 
         * @static
         * @member {number} spinSpeedFactor
         * @memberof module:solo.app.navigator
         */
        'spinSpeedFactor': {
            get: function () { return HEAPF32[Module._tiramisu_getNavigatonSpeed() >>> 2]; },
            set: function (value) { HEAPF32[Module._tiramisu_getNavigatonSpeed() >>> 2] = value; },
            enumerable: true
        },
        /**
         * Sets or gets the pan speed factor.
         * 
         * @static
         * @member {number} panSpeedFactor
         * @memberof module:solo.app.navigator
         */
        'panSpeedFactor': {
            get: function () { return HEAPF32[(Module._tiramisu_getNavigatonSpeed() >>> 2) + 1]; },
            set: function (value) { HEAPF32[(Module._tiramisu_getNavigatonSpeed() >>> 2) + 1] = value; },
            enumerable: true
        },
        /**
         * Sets or gets the zoom speed factor.
         * 
         * @static
         * @member {number} zoomSpeedFactor
         * @memberof module:solo.app.navigator
         */
        'zoomSpeedFactor': {
            get: function () { return HEAPF32[(Module._tiramisu_getNavigatonSpeed() >>> 2) + 2]; },
            set: function (value) { HEAPF32[(Module._tiramisu_getNavigatonSpeed() >>> 2) + 2] = value; },
            enumerable: true
        },
        /**
         * Sets or gets the rotation speed factor.
         * 
         * @static
         * @member {number} rotationSpeedFactor
         * @memberof module:solo.app.navigator
         */
        'rotationSpeedFactor': {
            get: function () { return HEAPF32[(Module._tiramisu_getNavigatonSpeed() >>> 2) + 3]; },
            set: function (value) { HEAPF32[(Module._tiramisu_getNavigatonSpeed() >>> 2) + 3] = value; },
            enumerable: true
        },

        /**
         * Sets or gets the inertia deceleration factor.
         * 
         * @static
         * @member {number} inertiaDecelerationFactor
         * @memberof module:solo.app.navigator
         */
        'inertiaDecelerationFactor': {
            get: function () { return inertiaDecelerationFactor; },
            set: function (value) { 
                var v = +value;
                if (v > 0) {
                    inertiaDecelerationFactor = v;
                }
            },
            enumerable: true
        },
        /**
         * Enables or disables the inertia.
         * 
         * @static
         * @member {boolean} inertiaDisabled
         * @memberof module:solo.app.navigator
         */
        'inertiaDisabled': {
            get: function () { return inertiaDisabled; },
            set: function (value) { inertiaDisabled = !!value; },
            enumerable: true
        },
        /**
         * Enables or disables object fitting by double-clicking.
         * 
         * @static
         * @member {boolean} objectFitDisabled
         * @memberof module:solo.app.navigator
         */
        'objectFitDisabled': {
            get: function () { return objectFitDisabled; },
            set: function (value) { objectFitDisabled = !!value; },
            enumerable: true
        },
        /**
         * Enables or disables changing the center point.
         * 
         * @static
         * @member {boolean} changeCenterpointDisabled
         * @memberof module:solo.app.navigator
         */
        'changeCenterpointDisabled': {
            get: function () { return changeCenterpointDisabled; },
            set: function (value) { changeCenterpointDisabled = !!value; },
            enumerable: true
        },
    });
}

module.exports = Navigator;